import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Store, MapPin, Package, GraduationCap, Gift, Megaphone, Tag, ArrowRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import FormationCard from "@/components/FormationCard";
import { BundleCard } from "@/pages/Offres";
import { useSeo } from "@/lib/seo";

function PromoCard({ pub, index }) {
  const external = /^https?:\/\//.test(pub.link || "");
  const btn = pub.button_label || "En profiter";
  const Btn = () => pub.link ? (
    external ? (
      <a href={pub.link} target="_blank" rel="noreferrer" data-testid={`promo-btn-${pub.id}`} className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5">{btn} <ArrowRight className="h-4 w-4" /></a>
    ) : (
      <Link to={pub.link} data-testid={`promo-btn-${pub.id}`} className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5">{btn} <ArrowRight className="h-4 w-4" /></Link>
    )
  ) : null;

  return (
    <article style={{ animationDelay: `${index * 60}ms` }} data-testid={`promo-card-${pub.id}`}
      className="group flex animate-fade-up flex-col overflow-hidden rounded-3xl border border-line bg-white shadow-[0_10px_40px_rgba(0,0,0,0.06)] transition-transform duration-300 hover:-translate-y-1.5">
      <div className="relative h-44 overflow-hidden bg-slate-100">
        {pub.image_url ? (
          <img src={fileUrl(pub.image_url)} alt={pub.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <span className="flex h-full items-center justify-center text-slate-300"><Megaphone className="h-10 w-10" /></span>
        )}
        {pub.discount_label && (
          <span className="absolute left-4 top-4 rounded-full bg-rose-500 px-3 py-1 text-sm font-black text-white shadow">{pub.discount_label}</span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-display text-lg font-black text-ink">{pub.title}</h3>
        {pub.description && <p className="mt-1.5 text-sm text-slate-500">{pub.description}</p>}
        {pub.body && <p className="mt-2 whitespace-pre-line text-xs text-slate-400">{pub.body}</p>}
        {pub.promo_code && (
          <div className="mt-3 flex items-center gap-2 rounded-xl border border-dashed border-brand/40 bg-brand-soft px-3 py-2">
            <Tag className="h-4 w-4 text-brand" />
            <span className="text-xs font-bold text-slate-500">Code :</span>
            <span className="font-mono text-sm font-black tracking-wider text-brand" data-testid={`promo-code-${pub.id}`}>{pub.promo_code}</span>
          </div>
        )}
        <div className="mt-auto"><Btn /></div>
      </div>
    </article>
  );
}

export default function VendorShopPage() {
  const { slug } = useParams();
  const [data, setData] = useState(undefined);

  useEffect(() => {
    let active = true;
    api.get(`/shop/${slug}`).then(({ data }) => active && setData(data)).catch(() => active && setData(null));
    return () => { active = false; };
  }, [slug]);

  useSeo({ title: data?.shop ? `${data.shop.shop_name} — Boutique ISMO-TECH` : "Boutique — ISMO-TECH" });

  if (data === undefined) return <div className="flex h-72 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>;
  if (!data) return (
    <div className="container-it py-24 text-center">
      <h1 className="font-display text-2xl font-bold text-ink">Boutique introuvable</h1>
      <Link to="/boutiques" className="mt-5 inline-block rounded-full bg-brand px-6 py-3 text-sm font-bold text-white">Voir les boutiques</Link>
    </div>
  );

  const { shop, products, formations, bundles = [], publications = [] } = data;
  return (
    <div className="pb-16" data-testid="vendor-shop-page">
      <section className="hero-shell relative overflow-hidden">
        <div className="hero-grid-lines absolute inset-0 opacity-60" />
        <div className="container-it relative z-10 flex flex-col items-start gap-5 py-12 sm:flex-row sm:items-center">
          <span className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl bg-white/10 text-white">
            {shop.shop_logo ? <img src={fileUrl(shop.shop_logo)} alt={shop.shop_name} className="h-full w-full object-cover" /> : <Store className="h-9 w-9" />}
          </span>
          <div>
            <h1 className="font-display text-3xl font-black text-white sm:text-4xl" data-testid="shop-name">{shop.shop_name}</h1>
            {shop.shop_description && <p className="mt-2 max-w-xl text-sm text-slate-300">{shop.shop_description}</p>}
            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs font-bold text-slate-300">
              {shop.country && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-brand" /> {shop.country}</span>}
              <span className="flex items-center gap-1.5"><Package className="h-3.5 w-3.5 text-brand" /> {shop.products_count} produits</span>
              <span className="flex items-center gap-1.5"><GraduationCap className="h-3.5 w-3.5 text-brand" /> {shop.formations_count} formations</span>
            </div>
          </div>
        </div>
      </section>

      {shop.shop_banner_active && shop.shop_banner_url && (
        <section className="container-it pt-8" data-testid="shop-banner">
          <div className="relative overflow-hidden rounded-3xl">
            <img src={fileUrl(shop.shop_banner_url)} alt={shop.shop_banner_title || "Promotion"} className="h-52 w-full object-cover sm:h-72" />
            <div className="absolute inset-0 bg-gradient-to-r from-ink/75 via-ink/35 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-center gap-3 p-6 sm:p-10">
              {shop.shop_banner_title && <h2 className="max-w-xl font-display text-2xl font-black leading-tight text-white sm:text-4xl" data-testid="shop-banner-title">{shop.shop_banner_title}</h2>}
              {shop.shop_banner_subtitle && <p className="max-w-lg text-sm text-white/85 sm:text-base">{shop.shop_banner_subtitle}</p>}
              {shop.shop_banner_link && (
                <a href={shop.shop_banner_link} data-testid="shop-banner-cta" className="mt-1 inline-flex w-fit items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-ink transition-transform duration-200 hover:-translate-y-0.5">
                  {shop.shop_banner_cta || "Découvrir"} <ArrowRight className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {publications.length > 0 && (
        <section className="container-it py-10" data-testid="shop-promotions">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-soft text-brand"><Megaphone className="h-5 w-5" /></span>
            <div>
              <span className="label-eyebrow text-brand">Nos offres spéciales</span>
              <h2 className="font-display text-xl font-extrabold text-ink sm:text-2xl">Promotions</h2>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {publications.map((p, i) => <PromoCard key={p.id} pub={p} index={i} />)}
          </div>
        </section>
      )}

      {bundles.length > 0 && (
        <section className="container-it py-6" data-testid="shop-bundles">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-soft text-brand"><Gift className="h-5 w-5" /></span>
            <div>
              <span className="label-eyebrow text-brand">Économisez plus</span>
              <h2 className="font-display text-xl font-extrabold text-ink sm:text-2xl">Offres groupées</h2>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {bundles.map((b, i) => <BundleCard key={b.id} bundle={b} index={i} />)}
          </div>
        </section>
      )}

      <section className="container-it py-10">
        <h2 className="font-display text-xl font-extrabold text-ink">Produits</h2>
        {products.length ? (
          <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        ) : <p className="mt-4 text-sm text-slate-400">Aucun produit publié.</p>}
      </section>

      {formations.length > 0 && (
        <section className="container-it py-4">
          <h2 className="font-display text-xl font-extrabold text-ink">Formations</h2>
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {formations.map((f, i) => <FormationCard key={f.id} formation={f} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
